import React from 'react';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormValidation from '@react-form-fields/material-ui/components/FormValidation';
import FieldText from '@react-form-fields/material-ui/components/Text';
import { FormComponent, IStateForm } from 'components/Abstract/Form';
import { ReactNode } from 'react';
import { WithStyles } from 'decorators/withStyles';
import authService from 'services/auth';
import { WithRouter } from 'decorators/withRouter';
import Snackbar from 'components/Shared/Snackbar';

type IProps = {
  classes?: any;
  onCancel: () => void;
  history?: any;
};

interface IState extends IStateForm<{ accessCode: string }> {
  loading: boolean;
  sessionId: string | null;
  expiresOn: Date | null;
  exceededAttempts: boolean;
  accessCodeExpired: boolean;
}

@WithStyles((theme) => ({
  buttons: {
    flexDirection: 'column',
  },
  button: {
    minWidth: 150,
    marginBottom: 6,
  },
  codeInput: {
    '-moz-appearance': 'textfield',
  },
}))
@WithRouter()
export class AccessCodeForm extends FormComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      ...this.state,
      loading: false,
      exceededAttempts: false,
      accessCodeExpired: false,
      sessionId: null,
      expiresOn: null,
    };
  }

  componentDidMount(): void {
    authService.loginStepState.sessionId.subscribe((sessionId) => {
      this.setState({ sessionId });
    });

    authService.loginStepState.timeout.subscribe((expiresOn) => {
      this.setState({ expiresOn });
    });
  }

  reSendCode() {
    if (!this.state.sessionId) return;
    this.setState({ loading: true });
    authService.reSendCode(this.state.sessionId).subscribe(
      () => {
        this.setState({ loading: false });
        Snackbar.show('Código reenviado com sucesso!');
      },
      (err) => {
        console.log('To aq', err.message);
        this.setState({ loading: false });
        Snackbar.error('Erro ao reenviar código!');
      }
    );
  }

  onSubmit() {
    if (!this.state.sessionId || !this.state.model.accessCode) return;

    this.setState({ loading: true });

    const next = () => {
      this.setState({ loading: false });
      Snackbar.show('Login efetuado com sucesso!');
      if (this.props.history) this.props.history.push('/');
    };

    const error = (err: any) => {
      this.setState({ loading: false });
      switch (err.message) {
      case 'invalid-access-code':
        Snackbar.error(
          'Código de acesso inválido, verifique se o código está correto e tente novamente.'
        );
        break;
      case 'access-code-max-attempts':
        Snackbar.error(
          'Você excedeu o número máximo de tentativas, tente novamente mais tarde.'
        );
        this.setState({ exceededAttempts: true });
        break;
      case 'access-code-expired':
        Snackbar.error(
          'Seu código de acesso expirou, solicite um novo código de acesso.'
        );
        this.setState({ accessCodeExpired: true });
        break;
      default:
        Snackbar.error(
          'Erro ao efetuar login, tente novamente mais tarde ou entre em contato com o suporte.'
        );
        break;
      }
    };

    authService
      .login(this.state.sessionId, this.state.model.accessCode)
      .subscribe({ next, error });
  }

  onCancel() {
    this.resetForm();
    this.setState({
      loading: false,
      model: { accessCode: '' },
      exceededAttempts: false,
      accessCodeExpired: false,
      sessionId: null,
      expiresOn: null,
    });
    if (this.props.onCancel) this.props.onCancel();
  }

  render(): ReactNode {
    const { classes } = this.props;
    const { model, loading, exceededAttempts, accessCodeExpired } = this.state;

    const userMessage = exceededAttempts
      ? 'Numero de tentativas excedido'
      : accessCodeExpired
        ? 'Código expirado'
        : '';

    return (
      <FormValidation
        onSubmit={this.onSubmit.bind(this)}
        ref={this.formValidationRef}
      >
        <Card elevation={0}>
          <CardContent>
            <FieldText
              label="Código de acesso"
              type="number"
              disabled={loading}
              value={model.accessCode}
              validation="required"
              onChange={this.updateModel((model, v) => (model.accessCode = v))}
              margin="dense"
            />
            {!!userMessage && <p>{userMessage}</p>}
          </CardContent>

          <CardActions className={classes.buttons}>
            <Button
              className={classes.button}
              disabled={loading}
              variant="contained"
              color="primary"
              type="submit"
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                ' Entrar'
              )}
            </Button>
            <Button
              className={classes.button}
              disabled={loading}
              onClick={this.onCancel.bind(this)}
            >
              Cancelar
            </Button>
            <Button
              className={classes.button}
              disabled={loading || exceededAttempts || accessCodeExpired}
              onClick={this.reSendCode.bind(this)}
            >
              Reenviar código
            </Button>
          </CardActions>
        </Card>
      </FormValidation>
    );
  }
}
